import type { AgentMode, RunnerMode, TaskContext } from "@project/shared";
import { baseDiffCommand, ciTriageChecklist, gateFailureModes } from "./prompt-formatters.js";

const SP_DESC_MAX_CHARS = 80;

function truncateDescription(desc: string, maxChars: number): string {
  if (desc.length <= maxChars) return desc;
  return desc.slice(0, maxChars) + "…";
}

type ProjectTag = NonNullable<TaskContext["projectTags"]>[number];

function formatTagWithContextPaths(tag: ProjectTag): string[] {
  const desc = tag.description ? ` — ${tag.description}` : "";
  const lines = [`- Name: "${tag.name}"${desc}`];
  for (const link of tag.contextPaths ?? []) {
    const label = link.label ? ` (${link.label})` : "";
    lines.push(`    → ${link.type}: ${link.path}${label}`);
  }
  return lines;
}

/** Living-estimate framing + risk legend, shared by every phase prompt. */
function buildEstimateReassessmentLines(context: TaskContext): string[] {
  const currentSp = context.taskStoryPointValue ?? "unset";
  const currentRisk = context.taskRiskLevel ?? "unset";
  return [
    `Story points and risk are LIVING estimates, not one-time labels. Reassess them at every phase — planning, building, review — against your current understanding, and adjust in EITHER direction: work that looks big early is often small once scoped, and vice versa. Lowering an inflated estimate is as valuable as raising an underestimate; a stale value is worse than a changed one.`,
    `Current values: story points ${currentSp}, risk ${currentRisk}.`,
    ``,
    `Risk levels (how much important surface the change touches):`,
    `- critical: foundational surface — auth, billing, data integrity, migrations`,
    `- high: important surface with broad blast radius`,
    `- medium: moderate, contained surface area`,
    `- low: small or isolated change`,
  ];
}

function buildPropertyInstructions(context: TaskContext, runnerMode?: RunnerMode): string[] {
  const isTask = runnerMode === "task";
  const parts: string[] = [];
  parts.push(
    ``,
    `### Proactive Property Management`,
    `As you work this task, proactively keep task properties accurate:`,
    `- Use update_task_properties to set any combination of: title, story points, risk, and tags`,
    `- You can update all properties at once or just one at a time as needed`,
    `- Icons are assigned automatically during identification — do not set icons manually`,
    ``,
    ...buildEstimateReassessmentLines(context),
    ``,
    `Don't wait for the user to ask — keep these accurate as the work takes shape.`,
    `If scope changes materially at any point, update the properties to match.`,
  );

  // Story point tier vocabulary — every phase agent may reassess SP, so all
  // runner modes get the tier list when the project defines one.
  if (context.storyPoints && context.storyPoints.length > 0) {
    parts.push(``, `Available story point tiers:`);
    for (const sp of context.storyPoints) {
      const desc = sp.description
        ? ` — ${truncateDescription(sp.description, SP_DESC_MAX_CHARS)}`
        : "";
      parts.push(`- Value ${sp.value}: "${sp.name}"${desc}`);
    }
  }

  if (context.projectTags && context.projectTags.length > 0) {
    const assignedIds = new Set(context.taskTagIds ?? []);
    const assigned = context.projectTags.filter((t) => assignedIds.has(t.id));
    const unassigned = context.projectTags.filter((t) => !assignedIds.has(t.id));

    if (assigned.length > 0) {
      parts.push(``, `Assigned tags:`);
      for (const tag of assigned) parts.push(...formatTagWithContextPaths(tag));
    }

    // Unassigned tags — PM/code-review only, include context paths so agents can see linked files
    if (!isTask && unassigned.length > 0) {
      parts.push(``, `Available project tags:`);
      for (const tag of unassigned) parts.push(...formatTagWithContextPaths(tag));
    }

    parts.push(
      ``,
      `Tags are the project glossary. call get_tag("<name>") for a term's full spec (overview, linked files, hierarchy) whenever a tag is assigned to this card or mentioned (@[tag:id]) in chat/plans. To mention a tag yourself, write @[tag:<name>] with the tag's exact name — the server rewrites it to the id token when it stores the text, so you never need the id. An unknown name is left as plain text. When your work changes how a tagged system behaves, update that tag's overview via update_tag with a short reason — your card is stamped into the revision history.`,
    );
  }

  return parts;
}

/**
 * Auto tasks boot straight into building with no plan-approval step — but the
 * plan must land on the card BEFORE code is written, so the team can see the
 * intent while the build runs. This section teaches plan-then-BUILD: saving the
 * plan is a required first step, NOT the deliverable — the implemented code is.
 * The single most common auto-mode failure is treating "posted a plan" (or
 * opening a plan-only PR) as done, so this section is emphatic that the plan is
 * only a step on the way to writing and shipping the actual code.
 */
function buildPlanDocumentationSection(context?: TaskContext): string[] {
  const hasPlan = !!context?.plan?.trim();
  return [
    ``,
    `### Plan first, then BUILD — the plan is a step, NOT the deliverable`,
    `- The card is already In Progress and advances automatically (In Progress → Review PR when you open the PR). There is no plan-approval step.`,
    ...(hasPlan
      ? [
          `- A plan is already saved on the card. Keep it current with update_task_plan if your approach diverges materially — then IMPLEMENT it in code. The saved plan is not the deliverable; the working implementation is.`,
        ]
      : [
          `- No plan is saved yet: BEFORE writing any code, investigate briefly (search first, read only critical files) and save a concise implementation plan with update_task_plan (file:line citations). Saving the plan is a planning step, not the goal — immediately move on to WRITING THE CODE that implements it; never pause for approval.`,
        ]),
    `- Your goal is to BUILD the change, not to produce a plan. After the plan is posted, actually implement it: edit source files, make the change work, then verify. Do NOT stop, go idle, or open a PR the moment the plan exists.`,
    `- Your pull request MUST contain the actual code implementation. Never open a plan-only or empty-diff PR — a PR that just records the plan is never the goal of auto mode unless the task explicitly asks ONLY for a plan. (If the task genuinely needs no code changes, don't open a PR at all — finish per the section below.)`,
    `- Identification auto-fills title, story points, and icon with quick AI guesses. After exploring, refine the title, story points, and risk with update_task_properties whenever they no longer match what the work actually is — adjust in either direction. Icons are automatic — never set them.`,
  ];
}

/**
 * Not every task produces code. Support requests, config/credential help,
 * questions, investigations, and research deliver an answer or a file — not a
 * diff. `create_pull_request` is only for tasks that change repo code, so this
 * section teaches leaf agents to NOT open an empty/throwaway PR just to satisfy
 * the workflow: deliver in chat/attachments and complete the card directly.
 */
function buildNoPrWhenNoCodeSection(baseBranch?: string): string[] {
  const diffCommand = baseDiffCommand(baseBranch);
  return [
    ``,
    `### A PR is NOT required — only open one for actual code changes`,
    `\`create_pull_request\` is for tasks that change code in the repo. Many tasks don't: support requests, config/credential help, answering a question, investigations, or research whose deliverable is an answer or a file rather than a diff.`,
    `- If you finish the work with NO code changes (an empty \`${diffCommand}\`), do NOT open a PR. An empty or throwaway PR just to "complete" the workflow is wrong — a human then has to close it.`,
    `- Deliver the result where it belongs: post the answer/config/findings with \`post_to_chat\`, and attach any files the user should keep with \`upload_attachment\` (any file type, up to 25MB). Never publish a deliverable as a Claude artifact or off-platform link — it belongs on the card.`,
    `- Then complete the card directly with \`force_update_task_status("Complete")\` — there is no PR or review step for a no-code task.`,
    `- When in doubt, check \`${diffCommand}\`: a real diff means open a PR; no diff means finish in chat and mark Complete.`,
  ];
}

/**
 * Corrects a recurring agent failure on UI/UX work: capturing a screenshot but
 * then NOT hosting it — usually excused with the false belief that "the
 * attachment server runs in a separate pod without codespace filesystem access,"
 * or that attachments are images-only. Both are wrong: `upload_attachment` reads
 * a codespace-local file (image OR video, ≤25MB) and hosts it on the card
 * (`tools/attachment-tools.ts`), and every pod ships headless Playwright chromium
 * for capture.
 *
 * This section makes visual proof a required, unprompted step for any
 * rendered-UI change, and it now requires TWO destinations, not one:
 * capture → attach-to-card → EMBED in the PR description. The card alone was
 * never enough — a reviewer reads the PR on GitHub, and "see the card" costs
 * them a context switch. This used to say there was "no programmatic path" to
 * put an image in a private-repo PR, which was simply wrong: `upload_attachment`
 * returns the file's capability download URL, our API serves those bytes to an
 * `<img>`, and GitHub renders (and caches) it through its image proxy.
 */
function buildAttachArtifactsSection(): string[] {
  return [
    ``,
    `### Screenshots & recordings — attach to the card AND embed in the PR description`,
    `If your diff changes anything a user sees rendered (e.g. \`apps/web/**\`, \`apps/sandbox/**\`, \`*.tsx\`/\`*.jsx\`, or styling), capture visual proof BEFORE you open the PR — a **screenshot** for a static change, or a short **screen recording** (\`.mp4\`/\`.webm\`/\`.gif\`, ≤25MB) for an interaction, animation, or multi-step flow. For a changed screen, capture before AND after. This is part of finishing UI work — do it unprompted, not only when a human asks. (Non-visual diffs and other documenting files — diagrams, generated reports — follow the same pattern when useful.)`,
    `Capture against the running preview: every pod ships headless Playwright chromium baked at \`~/.cache/ms-playwright/\`. Point it at the dev-server preview URL — \`page.screenshot({ path })\` for an image, a browser context \`recordVideo\` for a \`.webm\`.`,
    `Then attach it with \`upload_attachment\` (pass a workspace path): it reads the codespace-local file and hosts it on the card. It accepts images AND video (\`.mp4\`/\`.webm\`/\`.gif\`), up to 25MB.`,
    `- There is NO "separate pod / no filesystem access" limitation, and it is NOT images-only. Never tell the team a screenshot or recording "could not be hosted" — attach it.`,
    `- Pass \`tags\` when the capture is a good example of a glossary tag in some state — a screenshot of the tags page tagged \`tag\`, a board capture tagged \`task\`. Each tag's page lists its recent tagged attachments, so the label turns your screenshot into a living example of that entity and makes visual drift easy to spot. An unmatched name is reported back and never fails the upload.`,
    ``,
    `**The PR description must show the visual proof, not just point at it.** \`upload_attachment\` returns a \`downloadUrl\` for every file it stores, plus a ready-to-paste markdown line. Copy that line into the \`body\` you pass to \`create_pull_request\`:`,
    `- Images embed inline: \`![before — task board](<downloadUrl>)\`. Write a real caption in the alt text — "before"/"after", or what the shot demonstrates.`,
    `- Video and other non-image files do not render inline on GitHub. Link them instead: \`[interaction recording](<downloadUrl>)\`.`,
    `- Put them under a \`## Screenshots\` heading in the PR body, with before/after side by side (or one after the other) when you changed an existing screen.`,
    `- The URL is a signed capability our API serves; GitHub fetches and caches the image through its own proxy when the description renders, so it keeps working after the capability expires. Do NOT paste codespace-local file paths (\`/workspaces/repo/shot.png\`) — reviewers cannot open those.`,
    `- Keep attaching to the card as well. The card is the durable home for the capture and the only place \`tags\` apply; the PR description is what the reviewer actually reads. Do both — neither one replaces the other.`,
    `- If \`upload_attachment\` returns no \`downloadUrl\`, still attach to the card, and say plainly in the PR body that the embed URL was unavailable. Never silently ship a UI PR with no visual proof.`,
  ];
}

/**
 * The structured PR guide is authored by the BUILDER as part of opening the PR
 * (moved off the reviewer, 2026-07-24). This section teaches: publish it right
 * after create_pull_request, and re-publish for the new head SHA after every
 * push so the card's Guide tab never goes stale. It is best-effort — a failed
 * publish never blocks opening or updating the PR.
 */
function buildPrGuideSection(): string[] {
  return [
    ``,
    `### PR Guide — publish when you open the PR, refresh it on every push`,
    `Once your PR is open, publish a structured guide that walks a reviewer through the change with \`publish_review_guide\`. This is part of opening a PR: call it right after \`create_pull_request\` succeeds, and again whenever you push more commits to the branch.`,
    `- The head SHA is resolved from the local repo for you, so never hand-expand an abbreviated hash from commit output or plan text. Publish AFTER the push that created/updated the PR has landed on the branch.`,
    `- \`sections\` is a real top-level array ARGUMENT, not prose inside \`overview\`. Keep \`overview\` under 3000 characters — a short intro only — and put the walkthrough in \`sections\`, ordered with core behavior first and tests, migrations, generated files, and configuration later unless they are central.`,
    `- Explain each section's purpose and effect in plain language, and reference ONLY files this PR's diff actually changed — never context files you merely read or discussed.`,
    `- Anchors are optional and strict: the safest \`files\` entry is \`{"path": "..."}\` alone. If you anchor, \`hunkHeader\` must be the byte-exact full line from \`git diff <base>..HEAD -- <file> | grep '^@@'\` INCLUDING the trailing context text after the second \`@@\`, and \`startLine\`/\`endLine\` must overlap one real hunk's new-file range. When in doubt, omit the anchor fields.`,
    `- After any later push, call \`publish_review_guide\` again for the NEW head SHA — the card shows a "stale" banner until you republish for the current head.`,
    `- Best-effort: if publication fails after one corrected retry, continue. It never blocks opening or updating the PR.`,
  ];
}

/**
 * The pre-PR gate protocol — the ONE place this guidance is injected.
 *
 * It used to be a checklist ("verify, then open the PR") that silently
 * contradicted the repo's own "refresh your base right before opening the PR"
 * rule. Agents obeyed both, in that order — gates, then merge `dev`, then
 * re-run the gates because the base had moved — so a typical leaf card paid for
 * two or three full scoped passes. The fix is ordering, not exhortation: merge
 * the base BEFORE the single pass, then forbid anything that would invalidate
 * it. The turn prompt (`prompt-builder.ts`) deliberately carries only a one-line
 * pointer to this section rather than a second copy of it.
 */
function buildPrePrProtocolSection(context?: TaskContext): string[] {
  const base = context?.baseBranch?.trim() || "dev";
  return [
    `### Pre-PR Protocol — sync the base first, then ONE verification pass`,
    `CI runs the FULL suite (lint, typecheck, all test shards) on every PR — do not duplicate it locally. Local gates run ONCE, in this order:`,
    `1. Finish the implementation and commit it.`,
    `2. **Sync the base FIRST**: \`git fetch origin ${base} && git merge origin/${base} --no-edit\`. Run \`bun install\` afterwards only if the merge touched \`package.json\`/\`bun.lock\`. Merging before the gates is what makes one pass sufficient — merging after them is what forces a second run.`,
    `3. **One verification pass**: \`bun run check\` (lint + typecheck, fast, not lock-wrapped) then \`bun run test:affected\` (runs only the tests your diff can affect — apps/api diffs are expected to run unit tests only since CI covers the int shards; \`packages/shared\`/\`packages/db\`/root-config diffs escalate to the full suite automatically, which is still one pass, not a repeat). Selection is a heuristic — confirm it actually ran the packages you changed.`,
    `4. **Open the PR immediately** with \`mcp__conveyor__create_pull_request\`. Do NOT re-merge the base and do NOT re-run a gate that already passed. If \`${base}\` moved while the gates were running, open the PR anyway — CI validates against the merged base.`,
    `Docs/markdown/\`.claude\`-only diffs skip steps 2-3 entirely — open the PR and let CI validate.`,
    ``,
    `**When a gate fails: fix, re-run only the failing tests, confirm once.**`,
    `- Iterate on the failures directly with the package-local script — \`bun run --cwd <pkg> test:unit <files>\` (or \`test <files>\`). It skips turbo's dep-build and the singleton lock, so it costs seconds instead of minutes. Do NOT re-run the whole gate on each iteration.`,
    `- Pick that file set deliberately: \`rg <ComponentName>\` across \`__tests__\` to find every suite that transitively mounts what you changed, so the targeted loop is not blind to indirect mounters.`,
    `- Once the targeted files pass, run the full scoped gate ONE more time as final confirmation, then open the PR. That single confirmation run — not a full gate per iteration — is what catches a suite your targeted set missed.`,
    `- Do NOT open a PR with a known failing gate.`,
    ...gateFailureModes(),
    ``,
    `Also before you open the PR (neither one needs a gate re-run):`,
    `- Reassess story points and risk against the ACTUAL diff — the estimate was made at planning time; now you know what the work really was. Correct them with \`update_task_properties\` in either direction.`,
    `- **UI/UX diffs:** if you changed anything rendered (\`apps/web\`, \`apps/sandbox\`, \`*.tsx\`/\`*.jsx\`, styling), capture a screenshot (static change) or short screen recording (interaction/animation), attach it to the card with \`upload_attachment\`, and **embed the returned \`downloadUrl\` in the PR description** — both, not either — see "Screenshots & recordings" below. Skip only when the diff has no visual effect.`,
    `- For refactors: run \`${baseDiffCommand(context?.baseBranch)}\` and confirm the public API surface (exports, function signatures) has no unintended breaking changes.`,
    ``,
    `### Triaging a CI failure`,
    ...ciTriageChecklist(context?.baseBranch),
  ];
}

function buildExplorationMethodology(): string[] {
  return [
    ``,
    `### Exploration Methodology`,
    `Investigate efficiently — do not read files aimlessly:`,
    `- Search first, read second: use grep/glob to locate relevant code, then read only the files that matter`,
    `- Never re-read a file already in your context — you have a large context window, scroll up instead`,
    `- Start with 3-5 critical files, form a hypothesis about the approach, then validate with targeted reads`,
    `- Stop exploring when you can cite specific \`file.ts:line\` locations and function names for every step in your plan — that's enough`,
  ];
}

function buildPlanCitationFormat(): string[] {
  return [
    ``,
    `### Plan Citation Format`,
    `Plans must ground each change in the code. For every step that touches code:`,
    `- Cite the exact location as \`path/from/repo/root.ts:lineNumber\` (e.g. \`packages/conveyor-agent/src/execution/mode-prompt.ts:129\`).`,
    `- Name the specific function, class, constant, or JSX element being touched.`,
    `- When behavior hinges on a short piece of code, quote 1–3 lines inline instead of paraphrasing.`,
    `- Ranges are fine for larger edits (\`foo.ts:120-145\`). Do not cite whole files without a line.`,
    `- If a file doesn't exist yet, write \`NEW: path/to/new-file.ts\` and describe the surrounding module it fits into.`,
  ];
}

function buildDiscoveryPrompt(context?: TaskContext, runnerMode?: RunnerMode): string {
  const parts = [
    `\n## Mode: Discovery`,
    `You are in Discovery mode — helping plan and scope this task.`,
    `- You have read-only codebase access (can read files, run git commands, search code)`,
    `- Shell commands run without asking for approval — the pod is a sandbox, so investigate freely (build, test, query the dev DB, read logs). Only commands that would start the work are denied: git commit/push/merge/rebase/checkout, git apply, gh pr create, and package publish`,
    `- You can write plan files in .claude/plans/ only — no other file writes. Plan files on disk are NOT synced to the task; save the plan via update_task_plan`,
    `- Do NOT attempt to edit, write, or modify source code files — these operations will be denied`,
    `- If you identify code changes needed, describe them in the plan instead of implementing them`,
    `- You can create and manage subtasks`,
    `- Goal: collaborate with the user to create a clear plan`,
    `- Proactively fill task properties (SP, tags) as the plan takes shape`,
    ``,
    `### Planning Checklist (complete ALL before calling ExitPlanMode)`,
    `Your PRIMARY goal is to create a thorough plan. Complete these steps in order:`,
    `1. Read the task description and chat history — respond to what's been discussed`,
    `2. Investigate the codebase using the methodology below — search first, read targeted files`,
    `3. Save a detailed plan via \`update_task_plan\``,
    `4. Set story points, risk, tags, and title via \`update_task_properties\` (icon is set automatically)`,
    `5. Discuss the plan with the team if they're engaged, incorporate feedback`,
    `6. THEN call ExitPlanMode — it is the LAST step, not the first`,
    ...buildExplorationMethodology(),
    ...buildPlanCitationFormat(),
    ``,
    `### Self-Identification Tools`,
    `Use these MCP tools to set your own task properties:`,
    `- \`update_task_plan\` — save your plan and description`,
    `- \`update_task_properties\` — set title, story points, risk, and tags (any combination)`,
    `Note: Icons are assigned automatically during identification after planning is complete.`,
    ``,
    `### Tags & Context`,
    `- Early in discovery, identify relevant project tags that match this task's domain`,
    `- Add matching tags using \`update_task_properties\` — this links relevant documentation and rules that help you plan more effectively`,
    `- Tags accelerate discovery by surfacing domain-specific context automatically`,
    ``,
    ...(context?.isParentTask
      ? [
          `### Parent Task Coordination`,
          `You are a parent task with child tasks. Focus on breaking work into child tasks with detailed plans, not planning implementation for yourself.`,
          `- Use \`list_subtasks\` to review existing children. Create or update child tasks using \`create_subtask\` / \`update_subtask\`.`,
          `- Each child task should be a self-contained unit of work with a clear plan.`,
          `- Set ordering as explicit **card metadata**, not prose: pass \`dependsOn\` (sibling ids/slugs) on \`create_subtask\` for any child that blocks on another. Leave independent children with no dependencies so the pack runner fans them out in parallel. Do NOT encode "do X after Y" only in the plan text — the runner schedules off dependsOn, not narrative.`,
        ]
      : [
          `### Self-Update vs Subtasks`,
          `- If the work fits in a single task (1-3 SP), update YOUR OWN plan and properties — do not create subtasks`,
          `- Only create subtasks when the work genuinely requires multiple independent pieces (e.g., Pack-tier work, 8+ SP)`,
        ]),
    ``,
    `### Subtask Plan Requirements`,
    `When creating subtasks, each MUST include a detailed \`plan\` field:`,
    `- Plans should be multi-step implementation guides, not vague descriptions`,
    `- Include concrete \`file.ts:line\` citations, function/symbol names, and short code snippets where relevant`,
    `- Reference existing implementations when relevant (e.g., "follow the pattern in src/services/foo.ts")`,
    `- Include testing requirements and acceptance criteria`,
    `- Set \`storyPointValue\` based on estimated complexity`,
    `- Express cross-child ordering as \`dependsOn\` (sibling ids/slugs) on \`create_subtask\` — explicit dependency metadata, not "after task 2" phrasing in the plan. Independent children get no dependencies so they run in parallel.`,
    ``,
    `### Plan Verification Requirements`,
    `Every plan MUST include a **Testing / Verification** section so the build agent has a clear definition of "done". Enumerate:`,
    `- The scoped verification for the change: \`bun run check\` (lint + typecheck) plus \`bun run test:affected\` — name the specific package suites the diff will touch. Docs-only plans should state that no local gates are needed (CI validates on the PR).`,
    `- Any task-specific end-to-end checks (manual UI walk-through, API smoke test, migration dry-run, etc.)`,
    `You are NOT expected to run these gates yourself — discovery is read-only. Just describe them in the plan.`,
    ``,
    `### Completing Planning`,
    `Once ALL checklist items above are done, call the **ExitPlanMode** tool.`,
    `- Required before ExitPlanMode will succeed: **plan** (via update_task_plan), **story points**, **risk**, and **title** (via update_task_properties)`,
    `- ExitPlanMode validates these properties and marks planning as complete`,
    `- It does NOT start building — the team controls when to switch to Build mode`,
    `- Do NOT call ExitPlanMode until you have thoroughly explored the codebase and saved a detailed plan`,
  ];
  if (context) parts.push(...buildPropertyInstructions(context, runnerMode));
  return parts.join("\n");
}

function buildAutoPrompt(context?: TaskContext, runnerMode?: RunnerMode): string {
  const parts = [
    `\n## Mode: Auto`,
    `You are in Auto mode — operating autonomously through building → PR.`,
    `- You have full coding access (read, write, edit, bash, git) from the start — there is no read-only planning phase`,
    `- Safety rules: no destructive operations, use --force-with-lease instead of --force`,
    ...buildPlanDocumentationSection(context),
    ``,
    `### Subtask Plan Requirements`,
    `When creating subtasks, each MUST include a detailed \`plan\` field:`,
    `- Plans should be multi-step implementation guides, not vague descriptions`,
    `- Include concrete \`file.ts:line\` citations, function/symbol names, and short code snippets where relevant`,
    `- Reference existing implementations when relevant`,
    `- Include testing requirements and acceptance criteria`,
    `- Set \`storyPointValue\` based on estimated complexity`,
    `- Express cross-child ordering as \`dependsOn\` (sibling ids/slugs) on \`create_subtask\` — explicit dependency metadata, not "after task 2" phrasing in the plan. Independent children get no dependencies so they run in parallel.`,
    ``,
    ...buildPlanCitationFormat(),
    ``,
    ...(context?.isParentTask
      ? [
          ``,
          `### Parent Task Guidance`,
          `You are a parent task — coordinate child tasks instead of implementing directly.`,
          `If no children exist yet, break the work down now: save a parent-level plan with update_task_plan, then create child tasks with create_subtask (each with a detailed plan).`,
          `Child task status lifecycle: Open → InProgress → ReviewPR → ReviewDev → Complete.`,
          `Set child ordering with \`dependsOn\` (sibling ids/slugs) on \`create_subtask\` — explicit metadata the pack runner schedules off, not order described in plan text. Independent children get none and run in parallel.`,
        ]
      : [
          ...buildAttachArtifactsSection(),
          ...buildPrGuideSection(),
          ...buildNoPrWhenNoCodeSection(context?.baseBranch),
        ]),
    ``,
    `### Autonomous Guidelines:`,
    `- Make decisions independently — do not ask the team for approval at each step`,
    `- Only escalate when genuinely blocked (ambiguous requirements, missing access, conflicting instructions)`,
    `- Investigate efficiently: search (grep/glob) to locate code, read only critical files, form a hypothesis, validate, then plan`,
  ];
  if (context) parts.push(...buildPropertyInstructions(context, runnerMode));
  return parts.join("\n");
}

function buildBuildingPrompt(context?: TaskContext): string {
  const parts = [
    `\n## Mode: Building`,
    `You are in Building mode — executing the plan.`,
    `- You have full coding access (read, write, edit, bash, git)`,
    `- Safety rules: no destructive operations, use --force-with-lease instead of --force`,
    ...(context?.isParentTask
      ? [
          `- You are a parent task. Use \`list_subtasks\`, \`start_child_cloud_build\`, and subtask management tools to coordinate children.`,
          `- Do NOT implement code directly — fire child builds and review their work.`,
          `- Goal: coordinate child task execution and ensure all children complete successfully`,
        ]
      : [
          `- If this is a leaf task (no children): execute the plan directly`,
          `- Goal: implement the plan, run scoped verification once, open a PR when done`,
          ``,
          ...buildPrePrProtocolSection(context),
          ...buildAttachArtifactsSection(),
          ...buildPrGuideSection(),
          ...buildNoPrWhenNoCodeSection(context?.baseBranch),
          ...(context?.isAuto || !context?.plan?.trim()
            ? buildPlanDocumentationSection(context)
            : []),
        ]),
  ];
  if (context) parts.push(...buildPropertyInstructions(context));
  return parts.join("\n");
}

export function buildModePrompt(
  agentMode: AgentMode | null | undefined,
  context?: TaskContext,
  runnerMode?: RunnerMode,
): string | null {
  switch (agentMode) {
    case "discovery":
      return buildDiscoveryPrompt(context, runnerMode);
    case "building":
      return buildBuildingPrompt(context);
    case "review":
      return buildReviewPrompt(context);
    case "auto":
      return buildAutoPrompt(context, runnerMode);
    case "chat":
      return buildChatPrompt(context);
    default:
      return null;
  }
}

/**
 * Chat mode: a conversational assistant on its own card. The card boots
 * Unidentified and stays that way while the work is a conversation —
 * deliverables are files attached to the card, not a PR. Saving a plan is the
 * one escalation door: the first plan identifies the card, moves it to In
 * Progress (maybeIdentifyOnFirstPlan, api) and unlocks the PR path
 * (tool-access.ts gates create_pull_request and `git push` on that same plan).
 */
function buildChatPrompt(context?: TaskContext): string {
  const base = context?.baseBranch?.trim() || "dev";
  return [
    `\n## Mode: Chat`,
    `You are in Chat mode — a conversational assistant working directly with the user on this card.`,
    `- Respond conversationally to the user in chat. Ask clarifying questions when useful; this is a back-and-forth, not an autonomous build.`,
    `- You have full read/write access to the workspace and can run non-destructive shell commands, so you CAN create files (notes, scripts, docs, data, diagrams, etc.) when they help the user.`,
    `- This card starts Unidentified. That is correct — it stays a conversation until the work becomes a development task (see below).`,
    ``,
    `### Deliverables — attach files to the card`,
    `- When you create a file the user should keep, attach it to the card with the \`upload_attachment\` tool so it shows up on the card. Mention in chat what you attached.`,
    `- \`upload_attachment\` accepts any file type (markdown, PDF, HTML, text, data, images) up to 25MB — this is how docs are delivered. Do NOT publish deliverables as a Claude artifact or any other off-platform link; the card is the system of record.`,
    `- For conversational work, attachments are the deliverable. Do NOT open a PR to hand over a document or an answer.`,
    ``,
    `### Turning into a development task`,
    `- If the conversation becomes a request to change the code, escalate the card first: save a plan with \`update_task_plan\`. That first plan identifies the card and moves it to In Progress.`,
    `- The plan is also the gate on your PR path: \`create_pull_request\`, \`git push\` and \`gh pr\` are DENIED until a plan is saved, and allowed afterwards. Do not try to work around a denial — save the plan.`,
    `- Once the plan is saved, work like a normal build: implement it, then run ONE verification pass (\`bun run check\` plus \`bun run test:affected\`) after syncing the base (\`git fetch origin ${base} && git merge origin/${base} --no-edit\`), then open the PR with \`mcp__conveyor__create_pull_request\`.`,
    `- Keep talking to the user while you build — this is still their card.`,
    ``,
    `### Finishing the conversation`,
    `- When the user indicates they are done (or explicitly asks to wrap up / close the card), call \`force_update_task_status\` with status \`"Complete"\`. It works from either status, so a card still sitting Unidentified can be completed the same way as one that reached In Progress.`,
    `- If you opened a PR, do NOT complete the card — the PR review flow takes it from there.`,
    `- If the user is still engaged, keep helping — only complete the card once the interaction has concluded.`,
    `- Do not complete the card while you still owe the user a response or an attachment.`,
  ].join("\n");
}

/**
 * The card's assigned tags, framed for a reviewer.
 *
 * Review is the only build-capable mode that never calls
 * `buildPropertyInstructions`, so without this section a reviewer sees no tag
 * listing at all — and the task brief's "Reference Guides" section skips any
 * tag that has no contextPaths and no overview, which makes description-only
 * tags invisible. This listing restores them and tells the reviewer what the
 * glossary is FOR when judging a diff.
 */
function buildReviewTagSection(context?: TaskContext): string[] {
  const assignedIds = new Set(context?.taskTagIds ?? []);
  const assigned = (context?.projectTags ?? []).filter((t) => assignedIds.has(t.id));
  if (assigned.length === 0) return [];

  const parts = [`### Card Tags & Domain Context`, `This card carries these project glossary tags:`];
  for (const tag of assigned) parts.push(...formatTagWithContextPaths(tag));
  parts.push(
    ``,
    `A tag's rules and overview define the conventions the code under review must follow. Use them when you judge Pattern Consistency:`,
    `- Read the tag entries that match the files in this diff. The "Reference Guides" section of the task brief lists the same tags with a one-line summary of each linked doc.`,
    `- Call get_tag("<name>") for a term's full spec (overview, linked files, hierarchy) when the linked docs are not enough.`,
    `- A change that contradicts a tagged system's documented conventions is a review finding, even when it reads fine on its own.`,
    `- Skip the tags this diff does not touch — do not read them all up front.`,
    `- If the diff changes how a tagged system behaves and the tag's overview is now wrong, say so in your review.`,
    ``,
  );
  return parts;
}

// oxlint-disable-next-line max-lines-per-function -- prompt construction is a single cohesive block
function buildReviewPrompt(context?: TaskContext): string {
  const parts = [
    `\n## Mode: Review`,
    `You are in Review mode — performing code review with fix capability.`,
    `- You have full write access — you can audit code, make fixes, push changes, and run tests`,
    `- Safety rules: no destructive operations, use --force-with-lease instead of --force`,
    ``,
  ];

  if (context?.isParentTask) {
    parts.push(
      `### Parent Task Review`,
      `You are reviewing and coordinating child tasks.`,
      `- Use \`list_subtasks\` to see current child task state and progress.`,
      `- For children in ReviewPR status: review their code quality and merge with \`approve_and_merge_pr\`.`,
      `- For children with failing CI: check with \`get_execution_logs(childTaskId)\` and escalate if stuck.`,
      `- Fire next child builds with \`start_child_cloud_build\` when ready.`,
      `- Create follow-up tasks for issues discovered during review.`,
      ``,
      `### Coordination Workflow`,
      `1. Check child task statuses with \`list_subtasks\``,
      `2. Review completed children — check PRs, run tests if needed`,
      `3. Approve and merge passing PRs`,
      `4. Fire builds for children that are ready`,
      `5. Create follow-up tasks for anything out of scope`,
      `6. As children complete, correct their story points with update_subtask (storyPointValue) when the actual work diverged from the estimate — either direction`,
    );
  } else {
    const tagSection = buildReviewTagSection(context);
    const patternConsistency = tagSection.length
      ? `- **Pattern Consistency**: Does the code follow existing patterns in the codebase? Check nearby files AND the card's tag rules/overviews (see "Card Tags & Domain Context" below).`
      : `- **Pattern Consistency**: Does the code follow existing patterns in the codebase? Check nearby files.`;
    parts.push(
      `### Code Review Process`,
      `1. Run \`${baseDiffCommand(context?.baseBranch)}\` to see all changes in this PR`,
      `2. Read the task plan to understand the intended changes`,
      `3. Explore the surrounding codebase to verify pattern consistency`,
      `4. Review against the criteria below`,
      ``,
      `### Review Criteria`,
      `- **Correctness**: Does the code do what the plan says? Logic errors, off-by-one, race conditions?`,
      patternConsistency,
      `- **Security**: No hardcoded secrets, no injection vulnerabilities, proper input validation at boundaries.`,
      `- **Performance**: No unnecessary loops, no N+1 queries, no blocking in async contexts.`,
      `- **Error Handling**: Appropriate error handling at system boundaries. No swallowed errors.`,
      `- **Test Coverage**: Are new code paths tested? Edge cases covered?`,
      `- **TypeScript Best Practices**: Proper typing (no unnecessary \`any\`), correct React patterns, proper async/await.`,
      `- **Naming & Readability**: Clear names, no misleading comments, self-documenting code.`,
      ``,
      `### Fix Capability`,
      `You have full write access. If you find issues:`,
      `- **Small fixes**: Make the fix directly, commit, and push. Then re-review.`,
      `- **Larger issues**: Use \`request_code_changes\` to flag them for the team.`,
      `- After pushing fixes, wait for CI to pass before approving.`,
      ``,
      `### Output — You MUST do exactly ONE of:`,
      ``,
      `#### If code passes review (or after you've fixed all issues):`,
      `Use the \`approve_code_review\` tool with a brief summary of what looks good.`,
      ``,
      `#### If changes are needed that you cannot fix:`,
      `Use the \`request_code_changes\` tool with specific issues:`,
      `- Reference specific files and line numbers`,
      `- Explain what's wrong and suggest fixes`,
      `- Focus on substantive issues, not style nitpicks (linting handles that)`,
      ``,
      `#### Risk level (required on BOTH tools):`,
      `Every verdict MUST include a \`risk\` level — judge it by the surface area the change touches:`,
      `- \`critical\`: touches critical/foundational surface (auth, billing, data integrity, migrations)`,
      `- \`high\`: touches important surface with broad blast radius`,
      `- \`medium\`: moderate, contained surface area`,
      `- \`low\`: small or isolated change`,
      `The task may already have a risk level set. If your review makes you disagree with it, set the level you believe is correct — you have the authority to override it in either direction.`,
      ``,
      `#### Story points (correct if wrong):`,
      `You can see the full final diff — if the story-point estimate no longer matches the actual size of the work, correct it with update_task_properties (storyPointValue), in either direction. The card should record what the work actually was, not what it looked like at planning time.`,
      ``,
      ...tagSection,
      `### Previous Review Feedback`,
      `If previous review feedback is present in the chat history, verify those specific issues were addressed before raising new concerns.`,
      ``,
      `### Rules`,
      `- Do NOT re-review things CI already validates (formatting, lint rules).`,
      `- Be concise — actionable specifics over general observations.`,
      `- Max 5-7 issues per review. Prioritize the most important ones.`,
    );
  }

  return parts.join("\n");
}
